import { FC } from 'react'

import { ORDER_EXPIRY, ORDER_TYPE } from '@/common/constants'
import { InstrumentT, RewardType } from '@/common/types'
import Money from '@/common/models/money'
import useTradeStore from '@/trading/tradeStore'
import { cn, tradeEstimatedBrokerage } from '@/common/utils/utils'
import { Gift } from 'lucide-react'
import { OrderRewardItem } from './orderRewardItem'
import { REWARDS_COPY } from '@/locales/en'

type TradeReviewItemProps = {
    label: string
    value: string | number
    className?: string
}
const Item: FC<TradeReviewItemProps> = ({ label, value, className }) => {
    return (
        <li className={cn('flex justify-between py-3 border-b', className)}>
            <span className="text-black text-sm font-normal font-['Inter'] leading-tight">{label}</span>
            <strong className="text-right ml-2">{value}</strong>
        </li>
    )
}

type Props = {
    instrument: InstrumentT
}
export const ReviewItems: FC<Props> = ({ instrument }) => {
    const orderType = useTradeStore((state) => state.orderType)
    const limitPrice = useTradeStore((state) => state.limitPrice)
    const orderExpiry = useTradeStore((state) => state.orderExpiry)
    const numberOfShares = useTradeStore((state) => state.numberOfShares)
    const instrumentPrice = useTradeStore((state) => state.instrumentPrice)
    const promotion = useTradeStore((state) => state.promotion)
    const action = useTradeStore((state) => state.orderAction)
    if (!instrumentPrice || !numberOfShares || !orderType) return null
    const sharesAmount = Money.of(
        orderType === 'LIMIT' ? limitPrice : action === 'SELL' ? instrumentPrice : instrumentPrice * 1.02,
    ).multiply(numberOfShares)
    const fee = tradeEstimatedBrokerage(sharesAmount)
    const freeTrade = promotion?.promotionType === RewardType.FREE_TRADE_REBATE
    const totalAmount = action === 'SELL' ? sharesAmount.subtract(fee) : sharesAmount.add(fee)
    const decimalPrecisionToDisplay = limitPrice?.toString().split('.')[1]?.length || 2
    return (
        <ul>
            <Item
                label="Security"
                value={
                    instrument.securityName
                        ? `${instrument.securityName} (${instrument.securityCode})`
                        : instrument.securityCode
                }
            />
            <Item label="Order Direction" value={action === 'BUY' ? 'Buy' : 'Sell'} />
            <Item label="Order Type" value={ORDER_TYPE[orderType]} />
            <Item label="Expiry Duration" value={ORDER_EXPIRY[orderExpiry]} />
            <Item label="Shares" value={`${numberOfShares} shares`} />
            {orderType === 'LIMIT' && limitPrice && (
                <Item label="Price per unit" value={`${Money.of(limitPrice).toString(decimalPrecisionToDisplay)}`} />
            )}
            <Item label="Estimated Amount" value={`${sharesAmount.toString(decimalPrecisionToDisplay)}`} />
            <Item
                label={`Estimated Brokerage`}
                value={fee.toString()}
                className={freeTrade ? 'border-none pb-0 ' : ''}
            />
            {freeTrade && (
                <div className="border-b pb-4">
                    <OrderRewardItem
                        icon={<Gift size={20} />}
                        title={REWARDS_COPY.freeTradeItem.title}
                        description={REWARDS_COPY.freeTradeItem.description}
                    />
                </div>
            )}
            <Item label="Total" value={`${totalAmount.toString(decimalPrecisionToDisplay)}`} />
        </ul>
    )
}
